import {
  AppCard,
  Button,
  Icon,
  InfoField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiPencil } from "@mdi/js";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getShowChangeMaterialRequestQueryKey,
  useShowChangeMaterialRequest,
} from "../../../../../__generated__/orval/endpoints/change-material-requests/change-material-requests";
import { MmHistoryLogs } from "../../../../../components/molecules/MmHistoryLogs";
import { MmRequestAttachments } from "../../../../../components/molecules/MmRequestAttachments";
import { MmRequestHeader } from "../../../../../components/molecules/MmRequestHeader";
import { MmRequestComments } from "../../../../../components/molecules/MmRequestsComments";
import {
  MmRequestTabs,
  type MmRequestTabValue,
} from "../../../../../components/molecules/MmRequestTabs";
import { useAuth } from "../../../../../hooks/useAuth";
import { mmRequestTabsSearchSchema } from "../../mm_requests/-schemas/mmRequestTabsSearchSchema";

const ShowChangeMaterialRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  const { changeMaterialRequestId } = Route.useParams();
  const { tab } = Route.useSearch();
  const id = Number(changeMaterialRequestId);

  const { data, isLoading, isError } = useShowChangeMaterialRequest(id);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  const handleTabChange = useCallback(
    (newValue: MmRequestTabValue) => {
      if (newValue === "materialList") return;

      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  const handleEdit = useCallback(() => {
    navigate({
      params: { changeMaterialRequestId },
      to: "/vbuyer/change_material_requests/$changeMaterialRequestId/edit",
    });
  }, [changeMaterialRequestId, navigate]);

  const mmRequest = data?.mm_request;
  const mmRequestId = mmRequest?.id;
  const isCompleted = mmRequest?.process_instance?.completed ?? false;
  const mmRequestWithPreviousTicket = mmRequest as
    | (typeof mmRequest & { previous_ticket_id?: number | null })
    | undefined;

  const materialNumber = useMemo(() => {
    if (!Array.isArray(mmRequest?.matnumber)) {
      return "";
    }

    return mmRequest.matnumber.filter((value) => value.length > 0).join(", ");
  }, [mmRequest?.matnumber]);

  const changeTypeLabel = useMemo(() => {
    const changeType = (data as { change_type?: string | null } | undefined)
      ?.change_type;

    if (!changeType) return "-";

    const translationKey =
      `mmRequestChild.changeMaterial.changeTypeOptions.${changeType}` as const;

    return t(translationKey) || changeType;
  }, [data, t]);

  const userCanEdit = useMemo(() => {
    if (!user || !mmRequest) return false;

    return (
      (user.roles_names.includes("Edit material requests") ||
        user.id === mmRequest.assigned_to?.id) &&
      !mmRequest.process_instance?.completed
    );
  }, [mmRequest, user]);

  const showValueCurrent = Boolean(data?.value_current);
  const showValueNew = Boolean(data?.value_new);

  const title = mmRequestId
    ? `${t("common.changeMaterialRequest")} #${mmRequestId}`
    : t("common.changeMaterialRequest");

  if (isLoading) {
    return (
      <PageContainer showBackButton title={title}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer showBackButton title={title}>
      {!!mmRequest && (
        <MmRequestHeader
          id={mmRequest.id}
          keysToInvalidate={[...getShowChangeMaterialRequestQueryKey(id)]}
        />
      )}

      <AppCard>
        <MmRequestTabs onChange={handleTabChange} value={tab} />
        <Box sx={{ marginTop: "32px" }}>
          {tab === "details" && mmRequest && (
            <AppCard
              header={
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">{t("details")}</Typography>
                  {userCanEdit && (
                    <Button
                      onClick={handleEdit}
                      startIcon={<Icon icon={mdiPencil} />}
                      variant="outlined"
                    >
                      {t("common.edit")}
                    </Button>
                  )}
                </Box>
              }
            >
              <InfoField
                label={t("issuesOfRfq.previousTicket")}
                text={
                  mmRequestWithPreviousTicket?.previous_ticket_id
                    ? String(mmRequestWithPreviousTicket.previous_ticket_id)
                    : "-"
                }
              />
              <InfoField
                label={t("mmRequestChild.requesterField")}
                text={mmRequest.requester_field || "-"}
              />
              <InfoField
                label={t("mmRequestChild.requesterArea")}
                text={mmRequest.requester_area || "-"}
              />
              {!!data?.serial_number && (
                <InfoField
                  label={t("mmRequestChild.serialNumber")}
                  text={data.serial_number}
                />
              )}
              <InfoField
                label={t("issuesOfRfq.material")}
                text={materialNumber || "-"}
              />
              <InfoField
                label={t("mmRequestChild.subject")}
                text={mmRequest.subject || "-"}
              />
              <InfoField
                label={t("mmRequestChild.description")}
                text={mmRequest.description || "-"}
                variant="boxed"
              />
              <InfoField
                label={t("mmRequestChild.ritm")}
                text={data?.ritm || "-"}
              />
              <InfoField
                label={t("mmRequestChild.changeMaterial.changeType")}
                text={changeTypeLabel}
              />
              <InfoField
                label={t("mmRequestChild.typeValue")}
                text={data?.identified_problem_type?.title || "-"}
              />

              {showValueCurrent && showValueNew ? (
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <InfoField
                      label={t("mmRequestChild.valueCurrent")}
                      text={data?.value_current || "-"}
                    />
                  </Grid>
                  <Grid size={6}>
                    <InfoField
                      label={t("mmRequestChild.valueNew")}
                      text={data?.value_new || "-"}
                    />
                  </Grid>
                </Grid>
              ) : (
                <>
                  {showValueCurrent && (
                    <InfoField
                      label={t("mmRequestChild.valueCurrent")}
                      text={data?.value_current || "-"}
                    />
                  )}
                  {showValueNew && (
                    <InfoField
                      label={t("mmRequestChild.valueNew")}
                      text={data?.value_new || "-"}
                    />
                  )}
                </>
              )}
            </AppCard>
          )}
          {tab === "attachments" && mmRequestId && (
            <MmRequestAttachments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "comments" && mmRequestId && (
            <MmRequestComments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "history" && mmRequestId && (
            <MmHistoryLogs mmRequestId={mmRequestId} />
          )}
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/change_material_requests/$changeMaterialRequestId/",
)({
  component: ShowChangeMaterialRequest,
  validateSearch: mmRequestTabsSearchSchema,
});
